<template>
    <div class="sod-eod-full-report">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
            <div>
                <h1 class="text-h5 font-weight-bold mb-1">{{ heading }}</h1>
                <p class="text-body-2 grey--text mb-0">
                    Summary, trends and history for the selected date range.
                </p>
            </div>
            <v-spacer />
            <SodEodDateRangePicker @change="onRangeChange" />
        </div>

        <SodEodReportSummary :report="report" :loading="isLoadingSodEodReport() || isLoadingReport" class="mb-4" />

        <v-row>
            <v-col cols="12" md="5" lg="4">
                <v-card outlined rounded="lg" class="pa-4 mb-4 report-panel">
                    <SodEodWorkloadCalendar :entries="historyEntries" />
                </v-card>
            </v-col>
            <v-col cols="12" md="7" lg="8">
                <div class="d-flex justify-end mb-2">
                    <v-btn outlined rounded color="primary" small :loading="historyLoading" @click="loadHistory">
                        <v-icon left small>mdi-refresh</v-icon>
                        Refresh history
                    </v-btn>
                </div>
                <SodEodHistoryList :entries="historyEntries" :loading="historyLoading" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import SodEodDateRangePicker from './SodEodDateRangePicker.vue';
import SodEodReportSummary from './SodEodReportSummary.vue';
import SodEodHistoryList from './SodEodHistoryList.vue';
import SodEodWorkloadCalendar from './SodEodWorkloadCalendar.vue';

export default {
    name: 'SodEodFullReport',
    components: {
        SodEodDateRangePicker,
        SodEodReportSummary,
        SodEodHistoryList,
        SodEodWorkloadCalendar,
    },
    props: {
        // null/undefined = the current user's own report. A number switches
        // this into a manager's read-only drill-down for that team member,
        // reusing the exact same UI/aggregation as the self report.
        userId: {
            type: Number,
            default: null,
        },
        userName: {
            type: String,
            default: '',
        },
    },
    inject: {
        mySodEodHistory: { from: 'mySodEodHistory', default: () => () => [] },
        refreshMySodEodHistory: { from: 'refreshMySodEodHistory', default: () => async () => [] },
        managedSodEodHistory: { from: 'managedSodEodHistory', default: () => () => [] },
        refreshManagedSodEodHistory: { from: 'refreshManagedSodEodHistory', default: () => async () => [] },
        sodEodReport: { from: 'sodEodReport', default: () => () => null },
        getSodEodReport: { from: 'getSodEodReport', default: () => async () => null },
        managedSodEodReport: { from: 'managedSodEodReport', default: () => () => null },
        getManagedSodEodReport: { from: 'getManagedSodEodReport', default: () => async () => null },
        isLoadingSodEodReport: { from: 'isLoadingSodEodReport', default: () => () => false },
    },
    data() {
        return {
            dateFrom: null,
            dateTo: null,
            historyLoading: false,
            isLoadingReport: false,
        };
    },
    computed: {
        isSelf() {
            return !this.userId;
        },
        heading() {
            if (this.isSelf) return 'My Start of Day / End of Day Report';
            return this.userName ? `${this.userName}'s SOD/EOD Report` : 'SOD/EOD Report';
        },
        report() {
            return this.isSelf ? this.sodEodReport() : this.managedSodEodReport();
        },
        historyEntries() {
            return this.isSelf ? this.mySodEodHistory() : this.managedSodEodHistory();
        },
    },
    watch: {
        userId() {
            this.loadReport();
            this.loadHistory();
        },
    },
    mounted() {
        this.loadHistory();
    },
    methods: {
        onRangeChange(range) {
            this.dateFrom = range.dateFrom;
            this.dateTo = range.dateTo;
            this.loadReport();
        },
        async loadReport() {
            if (!this.dateFrom || !this.dateTo) return;
            this.isLoadingReport = true;
            try {
                if (this.isSelf) {
                    await this.getSodEodReport(this.dateFrom, this.dateTo);
                } else if (this.userId) {
                    await this.getManagedSodEodReport(this.userId, this.dateFrom, this.dateTo);
                }
            } finally {
                this.isLoadingReport = false;
            }
        },
        async loadHistory() {
            this.historyLoading = true;
            try {
                if (this.isSelf) {
                    await this.refreshMySodEodHistory();
                } else if (this.userId) {
                    await this.refreshManagedSodEodHistory(this.userId);
                }
            } finally {
                this.historyLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.report-panel {
    border-color: rgba(0, 0, 0, 0.08) !important;
}
</style>
